import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, Building, Send, CheckCircle2, AlertCircle, User } from 'lucide-react';

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot field
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function fetchPage() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/by-slug/da/kontakt');
        if (res.ok) {
          const data = await res.json();
          setPageData(data);
        }
      } catch (err) {
        console.error('Error fetching contact page:', err);
      }
    }
    fetchPage();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus({ type: 'error', message: 'Udfyld venligst alle påkrævede felter.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Mange tak for din henvendelse! Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.'
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        throw new Error('Serverfejl');
      }
    } catch (err) {
      setStatus({
        type: 'error',
        message: 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller kontakt os direkte på llk@carpa.dk.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = pageData?.title?.da || pageData?.title || "Kontakt - CARPA ApS";
  const desc = pageData?.meta_description?.da || pageData?.meta_description || "Kontakt CARPA ApS for en uforpligtende snak om jeres næste projekt. Vi er klar til at hjælpe jer sikkert i drift.";

  return (
    <div className="bg-[#f7f8fa] text-[#0e1f2f] min-h-screen py-16 md:py-24">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="mb-16 md:mb-24 max-w-3xl mx-auto text-center flex flex-col items-center">
          <span className="text-xs font-mono uppercase tracking-widest text-[#3c8888] mb-3 font-bold">
            {pageData?.hero_badge?.da || pageData?.hero_badge || "Kom i kontakt"}
          </span>
          <h1 className="text-4xl font-extrabold text-[#0e1f2f] tracking-tight sm:text-5xl">
            {pageData?.hero_title?.da || pageData?.hero_title || "Book en afklarende samtale"}
          </h1>
          <p className="mt-6 text-stone-600 text-base sm:text-lg leading-relaxed">
            {pageData?.hero_description?.da || pageData?.hero_description || "Har du spørgsmål til vores ydelser, eller ønsker du en uforpligtende snak om, hvordan vi kan hjælpe dit projekt sikkert i drift? Ring, send en e-mail eller udfyld formularen nedenfor."}
          </p>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left: Contact Info & Map */}
          <div className="lg:col-span-5 space-y-8">
            <div className="rounded-xl border border-[#0e1f2f]/10 bg-white p-6 md:p-8 space-y-6 shadow-sm">
              <h2 className="text-xl font-bold text-[#0e1f2f] tracking-tight">Kontaktoplysninger</h2>
              
              <ul className="space-y-4 text-sm text-stone-700">
                <li className="flex items-start gap-3">
                  <User className="h-5 w-5 text-[#3c8888] shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-[#0e1f2f]">Laust Kildevæld</strong>
                    <span className="text-xs text-stone-500 font-mono">Stifter | Maskinmester | Senior Consultant</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-[#3c8888] shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-[#0e1f2f]">Telefon</strong>
                    <a href="tel:+4553636223" className="hover:text-[#3c8888] transition-colors whitespace-nowrap">+45 53 63 62 23</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#3c8888] shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-[#0e1f2f]">E-mail</strong>
                    <a href="mailto:llk@carpa.dk" className="hover:text-[#3c8888] transition-colors whitespace-nowrap">llk@carpa.dk</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#3c8888] shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-[#0e1f2f]">Adresse</strong>
                    <span>Grævlinghøjen 12, 8800 Viborg</span>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-stone-500 pt-4 border-t border-stone-100">
                  <Building className="h-5 w-5 shrink-0 mt-0.5 text-stone-400" />
                  <div>
                    <strong className="block text-stone-600">Virksomhedsdata</strong>
                    <span>CARPA ApS · CVR: 43293354</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Google Maps Integration */}
            <div className="rounded-xl border border-[#0e1f2f]/10 overflow-hidden aspect-video bg-stone-100 shadow-sm">
              <iframe
                title="CARPA ApS Beliggenhed"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2222.883733055239!2d9.423985177245053!3d56.45239697321528!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464bf009f9024f2b%3A0x6335198df895123!2sGr%C3%A6vlingh%C3%B8jen%2012%2C%208800%20Viborg!5e0!3m2!1sda!2sdk!4v1716712345678!5m2!1sda!2sdk"
                className="h-full w-full border-0"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="lg:col-span-7 rounded-xl border border-[#0e1f2f]/10 bg-white p-6 md:p-8 shadow-sm">
            <h2 className="text-xl font-bold text-[#0e1f2f] tracking-tight mb-6">Send os en meddelelse</h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Antispam Honeypot Field - Absolutely hidden from humans */}
              <div className="sr-only absolute overflow-hidden h-0 w-0" aria-hidden="true">
                <label htmlFor="website_url">Do not fill this out if you are human</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Navn */}
                <div className="space-y-1.5">
                  <label htmlFor="name" className="text-xs font-mono uppercase tracking-wider text-stone-500 font-bold">
                    Navn <span className="text-[#3c8888]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full rounded-md border border-[#0e1f2f]/10 bg-[#f7f8fa] px-4 py-3 text-sm text-[#0e1f2f] placeholder-stone-400 focus:border-[#3c8888] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#3c8888] transition-colors"
                    placeholder="Dit fulde navn"
                  />
                </div>

                {/* Telefonnummer */}
                <div className="space-y-1.5">
                  <label htmlFor="phone" className="text-xs font-mono uppercase tracking-wider text-stone-500 font-bold">
                    Telefonnummer <span className="text-[#3c8888]">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full rounded-md border border-[#0e1f2f]/10 bg-[#f7f8fa] px-4 py-3 text-sm text-[#0e1f2f] placeholder-stone-400 focus:border-[#3c8888] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#3c8888] transition-colors"
                    placeholder="+45 00 00 00 00"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-stone-500 font-bold">
                  E-mail <span className="text-[#3c8888]">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full rounded-md border border-[#0e1f2f]/10 bg-[#f7f8fa] px-4 py-3 text-sm text-[#0e1f2f] placeholder-stone-400 focus:border-[#3c8888] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#3c8888] transition-colors"
                  placeholder="din.email@virksomhed.dk"
                />
              </div>

              {/* Beskrivelse */}
              <div className="space-y-1.5">
                <label htmlFor="description" className="text-xs font-mono uppercase tracking-wider text-stone-500 font-bold">
                  Beskrivelse af dit projekt eller behov <span className="text-[#3c8888]">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows="5"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full rounded-md border border-[#0e1f2f]/10 bg-[#f7f8fa] px-4 py-3 text-sm text-[#0e1f2f] placeholder-stone-400 focus:border-[#3c8888] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#3c8888] transition-colors resize-none"
                  placeholder="Beskriv dit anlæg, tidsplan eller spørgsmål..."
                />
              </div>

              {/* Status Message */}
              {status.message && (
                <div className={`rounded-md p-4 flex items-start gap-3 text-sm ${
                  status.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {status.type === 'success' ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" /> : <AlertCircle className="h-5 w-5 shrink-0 text-red-600" />}
                  <span>{status.message}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#3c8888] py-3.5 text-sm font-bold text-white shadow-md hover:bg-[#3c8888]/90 transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed border-b-2 border-[#3c8888]"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>Sender...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Send meddelelse</span>
                  </>
                )}
              </button>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
}
